import React, { useEffect, useState } from 'react';

const approvedCandidatesEndpoint =
  (process.env.REACT_APP_API_BASE || '') + 'candidateprofile/approved';

const getCandidateName = (candidate = {}) => {
  const fullName = [candidate.candidateFirstName, candidate.candidateLastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  return fullName || 'Approved Candidate';
};

const shuffleCandidates = (candidates = []) => {
  const shuffledCandidates = [...candidates];

  for (let currentIndex = shuffledCandidates.length - 1; currentIndex > 0; currentIndex -= 1) {
    const randomIndex = Math.floor(Math.random() * (currentIndex + 1));
    [shuffledCandidates[currentIndex], shuffledCandidates[randomIndex]] = [
      shuffledCandidates[randomIndex],
      shuffledCandidates[currentIndex]
    ];
  }

  return shuffledCandidates;
};

const groupCandidatesByBallot = (candidates = []) => {
  const groupedCandidates = candidates.reduce((groups, candidate) => {
    const ballotKey = `${candidate.ballotId}-${candidate.ballotName || 'ballot'}`;

    if (!groups[ballotKey]) {
      groups[ballotKey] = {
        ballotId: candidate.ballotId || 0,
        sortOrder: candidate.sortOrder || 0,
        ballotName: candidate.ballotName || 'Ballot',
        ballotDetail: candidate.ballotDetail || '',
        candidates: []
      };
    }

    groups[ballotKey].candidates.push(candidate);
    return groups;
  }, {});

  return Object.values(groupedCandidates)
    .sort((left, right) => {
      if (left.sortOrder !== right.sortOrder) {
        return left.sortOrder - right.sortOrder;
      }

      return left.ballotId - right.ballotId;
    })
    .map((ballotGroup) => ({
      ...ballotGroup,
      candidates: shuffleCandidates(ballotGroup.candidates)
    }));
};

const getImageSource = (candidate = {}) => {
  if (candidate.imageBlob && candidate.imageFileType) {
    return `data:${candidate.imageFileType};base64,${candidate.imageBlob}`;
  }

  return `${process.env.PUBLIC_URL}/no-picture-available-icon.jpg`;
};

const ViewAllApproved = () => {
  const [ballots, setBallots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    fetch(approvedCandidatesEndpoint, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Unable to load approved candidate profiles.');
        }

        return response.json();
      })
      .then((data) => {
        setBallots(groupCandidatesByBallot(Array.isArray(data) ? data : []));
        setErrorMessage('');
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }

        console.error('Error fetching approved candidates:', error);
        setErrorMessage(
          'Approved candidate profiles are unavailable right now. Please try again later.'
        );
      })
      .finally(() => {
        setIsLoading(false);
      });

    return () => controller.abort();
  }, []);

  return (
    <section className='approved-candidates-page'>
      <div className='approved-candidates-hero'>
        <p className='approved-candidates-kicker'>Public Candidate Gallery</p>
        <h1 className='approved-candidates-title'>
          Approved candidate statements and photos
        </h1>
        <p className='approved-candidates-intro'>
          Explore the approved profiles currently available for the election.
        </p>
        {!isLoading && !errorMessage ? (
          <p className='approved-candidates-count'>
            {ballots.reduce(
              (totalCandidates, ballot) => totalCandidates + ballot.candidates.length,
              0
            )}{' '}
            approved candidate
            {ballots.reduce(
              (totalCandidates, ballot) => totalCandidates + ballot.candidates.length,
              0
            ) === 1
              ? ''
              : 's'}
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <div className='approved-candidates-status' role='status'>
          Loading approved candidates...
        </div>
      ) : null}

      {!isLoading && errorMessage ? (
        <div
          className='approved-candidates-status approved-candidates-status-error'
          role='alert'
        >
          {errorMessage}
        </div>
      ) : null}

      {!isLoading && !errorMessage && ballots.length === 0 ? (
        <div className='approved-candidates-status'>
          No approved candidate profiles are available yet.
        </div>
      ) : null}

      {!isLoading && !errorMessage && ballots.length > 0 ? (
        <div className='approved-ballot-list'>
          {ballots.map((ballot) => (
            <section
              key={`${ballot.ballotId}-${ballot.ballotName}`}
              className='approved-ballot-section'
            >
              <div className='approved-ballot-header'>
                <p className='approved-ballot-label'>Ballot</p>
                <h2 className='approved-ballot-name'>{ballot.ballotName}</h2>
                {ballot.ballotDetail ? (
                  <p className='approved-ballot-detail'>{ballot.ballotDetail}</p>
                ) : null}
              </div>
              <div className='approved-candidates-grid'>
                {ballot.candidates.map((candidate, index) => {
                  const candidateName = getCandidateName(candidate);

                  return (
                    <article
                      key={`${ballot.ballotId}-${candidate.ballotCandidateName || candidateName}-${index}`}
                      className='approved-candidate-card'
                    >
                      <div className='approved-candidate-photo-frame'>
                        <img
                          className='approved-candidate-photo'
                          src={getImageSource(candidate)}
                          alt={`Candidate portrait for ${candidateName}`}
                        />
                      </div>
                      <div className='approved-candidate-body'>
                        <p className='approved-candidate-position'>
                          {candidate.ballotCandidateName || 'Candidate'}
                        </p>
                        <h3 className='approved-candidate-name'>{candidateName}</h3>
                        <p className='approved-candidate-statement'>
                          {candidate.candidateStatement ||
                            'No candidate statement was provided.'}
                        </p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default ViewAllApproved;
